import { useQuery } from 'react-query';
import { WorkspaceCacheKeys } from 'types/cacheKeys.types';
import { type IWorkspace, type IWorkspaceMember } from 'types/workspace.types';
import { WorkspaceMemberRole } from 'types/organization.types';
import useWorkspaceDetails from 'hooks/useWorkspaceDetails';
import { getAccountantWorkspaces } from 'api/v3/access/accountantsAuth';

interface UseWorkspacesReturn {
  workspaces: IWorkspace[];
  selectedWorkspace: IWorkspace | undefined;
  selectedWorkspaceOwner: IWorkspaceMember | undefined;
  hasWorkspaces: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  workspaceAdmins: IWorkspaceMember[] | undefined;
  workspaceSimpleMembers: IWorkspaceMember[] | undefined;
}

export const useWorkspaces = (): UseWorkspacesReturn => {
  const { userEmail, workspaceId: currentWorkspaceId } = useWorkspaceDetails();

  const query = useQuery({
    queryKey: [WorkspaceCacheKeys.Workspaces, userEmail],
    queryFn: getAccountantWorkspaces,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const workspaces: IWorkspace[] = query.data?.workspaces || [];

  const selectedWorkspace = workspaces.find(
    (workspace) => workspace._id === currentWorkspaceId,
  );

  // Safe access to workspace members - only if selectedWorkspace exists
  const workspaceOwner = selectedWorkspace?.members?.find(
    (member) => member.role === WorkspaceMemberRole.OWNER,
  );
  const workspaceAdmins = selectedWorkspace?.members?.filter(
    (member) => member.role === WorkspaceMemberRole.ADMIN,
  );
  const workspaceSimpleMembers = selectedWorkspace?.members?.filter(
    (member) => member.role === WorkspaceMemberRole.MEMBER,
  );

  return {
    workspaces,
    selectedWorkspace,
    hasWorkspaces: workspaces.length > 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
    selectedWorkspaceOwner: workspaceOwner,
    workspaceAdmins,
    workspaceSimpleMembers,
  };
};
